import { useContext, useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Card,
  Checkbox,
  Button,
  Space,
  Divider,
  message,
} from "antd";
import { SaveOutlined, SettingOutlined } from "@ant-design/icons";

import AdminSideBar from "../../components/SideBar/AdminSidebar";
import SideBar from "../../components/SideBar";
import TopBar from "../../components/TopBar";
import { AuthContext } from "../../context/AuthContext";
import { getReportsApi } from "../../services/PowerBI/PowerBIApis";
import {
  getReportAccessConfigApi,
  updateReportAccessConfigApi,
} from "../../services/Report/ReportApis";

const { Content } = Layout;

interface PowerBIReport {
  ReportID: string;
  DatasetID: string;
  ReportName: string;
  Section?: string;
  id?: string;
}

interface ReportEntry {
  id: string;
  label: string;
  category: string;
}

export const ADMIN_ALLOWED_REPORTS_KEY = "adminAllowedReports";

const AdminReportConfigPage: React.FC<{ adminLayout?: boolean }> = ({ adminLayout = true }) => {
  const { currentGroup } = useContext(AuthContext);
  const [reports, setReports] = useState<ReportEntry[]>([]);
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentGroup) return;
    const groupId = Number(currentGroup);
    Promise.all([
      getReportsApi().catch(() => ({ data: [] })),
      getReportAccessConfigApi(groupId).catch(() => null),
    ]).then(([pbiRes, configRes]: any) => {
      const list: PowerBIReport[] = Array.isArray(pbiRes) ? pbiRes : pbiRes?.data ?? [];
      const all: ReportEntry[] = list
        .filter((r) => r.ReportID && r.ReportID !== "undefined")
        .map((r) => ({
          id: `pbi-${r.ReportID}`,
          label: r.ReportName,
          category: r.Section || "Power BI",
        }));
      setReports(all);

      const cfg = configRes?.data?.Config ?? configRes?.data?.config;
      if (cfg && Array.isArray(cfg.enabledReports)) {
        setAllowed(new Set(cfg.enabledReports));
      } else {
        setAllowed(new Set(all.map((r) => r.id)));
      }
    }).finally(() => setLoading(false));
  }, [currentGroup]);

  const save = async () => {
    if (!currentGroup) return;
    setSaving(true);
    const groupId = Number(currentGroup);
    const enabledReports = Array.from(allowed);
    try {
      await updateReportAccessConfigApi(groupId, enabledReports);
      // Notify same-tab and cross-tab listeners
      try { new BroadcastChannel("reportAccessConfig").postMessage("updated"); } catch {}
      window.dispatchEvent(new Event("adminAllowedReportsChanged"));
      message.success("Report access config saved.");
    } catch {
      message.error("Failed to save config.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id: string, checked: boolean) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleCategory = (category: string, checked: boolean) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      reports
        .filter((r) => r.category === category)
        .forEach((r) => {
          if (checked) next.add(r.id);
          else next.delete(r.id);
        });
      return next;
    });
  };

  const selectAll = () => setAllowed(new Set(reports.map((r) => r.id)));
  const clearAll = () => setAllowed(new Set());

  const categories = Array.from(new Set(reports.map((r) => r.category)));

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {adminLayout ? <AdminSideBar /> : <SideBar />}
      <Layout className="site-layout">
        <TopBar />
        <Content>
          <div style={{ padding: 24, background: "#fff", minHeight: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <SettingOutlined style={{ color: "#f04f05", fontSize: 22 }} />
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Report Access Config
                </Typography.Title>
              </div>
              <Space>
                <Button onClick={clearAll}>Clear all</Button>
                <Button onClick={selectAll}>Select all</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                  Save
                </Button>
              </Space>
            </div>

            <Typography.Paragraph type="secondary">
              Choose which reports users are allowed to see on the Reports page.
              Users can still hide individual cards from their own view, but only
              the reports you enable here will be available to them.
            </Typography.Paragraph>

            <Card
              loading={loading}
              style={{ borderRadius: 12 }}
              bodyStyle={{ padding: 24 }}
            >
              {categories.map((cat) => {
                const items = reports.filter((r) => r.category === cat);
                const allOn = items.every((r) => allowed.has(r.id));
                const someOn = items.some((r) => allowed.has(r.id));
                return (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <Checkbox
                      checked={allOn}
                      indeterminate={!allOn && someOn}
                      onChange={(e) => toggleCategory(cat, e.target.checked)}
                    >
                      <strong>{cat}</strong>
                    </Checkbox>
                    <div style={{ paddingLeft: 24, marginTop: 8 }}>
                      <Space direction="vertical" size={6}>
                        {items.map((r) => (
                          <Checkbox
                            key={r.id}
                            checked={allowed.has(r.id)}
                            onChange={(e) => toggle(r.id, e.target.checked)}
                          >
                            {r.label}
                          </Checkbox>
                        ))}
                      </Space>
                    </div>
                    <Divider style={{ margin: "16px 0 0" }} />
                  </div>
                );
              })}
            </Card>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminReportConfigPage;
